import React, { useState, useCallback } from 'react';
import {
    DndContext,
    DragOverlay,
    DragStartEvent,
    DragEndEvent,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter
} from '@dnd-kit/core';
import { Move, CheckSquare, ArrowLeftRight, Loader } from 'lucide-react';
import { RackSummary, RackStructure } from '../rack-builder/types';
import { LocationHeatmapDto } from './types';
import { ActiveMoveBin } from './heatmapMoveTypes';
import { InteractiveRackBody } from './InteractiveRackBody';

interface DualRackViewProps {
    allRacks: RackSummary[];
    leftRackNumber: string | null;
    rightRackNumber: string | null;
    leftRackStructure: RackStructure | null;
    rightRackStructure: RackStructure | null;
    leftHeatmapData: Map<string, LocationHeatmapDto>;
    rightHeatmapData: Map<string, LocationHeatmapDto>;
    isLoadingLeft: boolean;
    isLoadingRight: boolean;

    onLeftRackChange: (rackNumber: string) => void;
    onRightRackChange: (rackNumber: string) => void;
    onSwap: () => void;

    onBinMoveRequested: (fromBinCode: string, toBinCode: string) => void;
    onBatchMoveRequested?: (sourceBinCodes: string[], toBinCode: string) => void;
    selectedBinCodes: Set<string>;
    onToggleSelect: (binCode: string, mode: 'single' | 'add' | 'range') => void;
    onBinContextMenu?: (binCode: string, x: number, y: number) => void;
    leftEmptyBinCodes?: Set<string>;
    rightEmptyBinCodes?: Set<string>;
    leftActiveMoveBins?: Map<string, ActiveMoveBin[]>;
    rightActiveMoveBins?: Map<string, ActiveMoveBin[]>;
}

/**
 * Cross-rack view with two racks side-by-side under a single DndContext.
 * Drag a bin from either rack and drop on any bin in either rack — works
 * across racks without any special handling since the DndContext covers both.
 */
export const DualRackView: React.FC<DualRackViewProps> = ({
    allRacks,
    leftRackNumber,
    rightRackNumber,
    leftRackStructure,
    rightRackStructure,
    leftHeatmapData,
    rightHeatmapData,
    isLoadingLeft,
    isLoadingRight,
    onLeftRackChange,
    onRightRackChange,
    onSwap,
    onBinMoveRequested,
    onBatchMoveRequested,
    selectedBinCodes,
    onToggleSelect,
    onBinContextMenu,
    leftEmptyBinCodes,
    rightEmptyBinCodes,
    leftActiveMoveBins,
    rightActiveMoveBins
}) => {
    const [activeBinCode, setActiveBinCode] = useState<string | null>(null);
    const [activeIsBatch, setActiveIsBatch] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
    );

    const handleDragStart = useCallback((event: DragStartEvent) => {
        const draggedBin = String(event.active.id);
        setActiveBinCode(draggedBin);
        setActiveIsBatch(selectedBinCodes.has(draggedBin) && selectedBinCodes.size > 1);
    }, [selectedBinCodes]);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const from = activeBinCode;
        const to = event.over ? String(event.over.id) : null;
        const wasBatch = activeIsBatch;
        setActiveBinCode(null);
        setActiveIsBatch(false);

        if (!from || !to || from === to) return;

        if (wasBatch && onBatchMoveRequested) {
            const sources = Array.from(selectedBinCodes).filter(b => b !== to);
            if (sources.length > 0) onBatchMoveRequested(sources, to);
        } else {
            onBinMoveRequested(from, to);
        }
    }, [activeBinCode, activeIsBatch, onBinMoveRequested, onBatchMoveRequested, selectedBinCodes]);

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <div className="w-full h-full flex flex-col">
                <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 rounded-lg mb-2 flex items-center space-x-2 flex-shrink-0">
                    <Move className="w-4 h-4" />
                    <span>
                        <strong>Dobbel-reol-modus:</strong> Dra mellom to reoler. Ctrl/Cmd+klikk for multi-valg. Høyreklikk for forslag / tøm-bin.
                    </span>
                </div>

                {/* Rack selectors */}
                <div className="flex items-center space-x-2 mb-2 flex-shrink-0">
                    <div className="flex-1">
                        <label className="block text-xs font-semibold text-gray-600 mb-0.5">Venstre reol</label>
                        <select
                            value={leftRackNumber || ''}
                            onChange={e => onLeftRackChange(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                        >
                            <option value="">— Velg reol —</option>
                            {allRacks.map(r => (
                                <option
                                    key={r.rackNumber}
                                    value={r.rackNumber}
                                    disabled={r.rackNumber === rightRackNumber}
                                >
                                    Reol {r.rackNumber} ({r.locationCount} lok)
                                </option>
                            ))}
                        </select>
                    </div>

                    <button
                        type="button"
                        onClick={onSwap}
                        disabled={!leftRackNumber || !rightRackNumber}
                        className="mt-4 p-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
                        title="Bytt om på reolene"
                    >
                        <ArrowLeftRight className="w-4 h-4 text-gray-700" />
                    </button>

                    <div className="flex-1">
                        <label className="block text-xs font-semibold text-gray-600 mb-0.5">Høyre reol</label>
                        <select
                            value={rightRackNumber || ''}
                            onChange={e => onRightRackChange(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                        >
                            <option value="">— Velg reol —</option>
                            {allRacks.map(r => (
                                <option
                                    key={r.rackNumber}
                                    value={r.rackNumber}
                                    disabled={r.rackNumber === leftRackNumber}
                                >
                                    Reol {r.rackNumber} ({r.locationCount} lok)
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Two rack bodies side-by-side */}
                <div className="flex-1 flex gap-2 min-h-0">
                    <div className="flex-1 min-w-0 flex flex-col border border-gray-300 rounded-lg overflow-hidden bg-white">
                        {isLoadingLeft ? (
                            <div className="flex-1 flex items-center justify-center">
                                <Loader className="w-5 h-5 animate-spin text-blue-600" />
                            </div>
                        ) : leftRackStructure ? (
                            <InteractiveRackBody
                                rackStructure={leftRackStructure}
                                heatmapData={leftHeatmapData}
                                activeBinCode={activeBinCode}
                                activeIsBatch={activeIsBatch}
                                selectedBinCodes={selectedBinCodes}
                                onToggleSelect={onToggleSelect}
                                onBinContextMenu={onBinContextMenu}
                                emptyBinCodes={leftEmptyBinCodes}
                                activeMoveBins={leftActiveMoveBins}
                                title={leftRackNumber ? `Reol ${leftRackNumber}` : 'Venstre'}
                            />
                        ) : (
                            <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
                                Velg en reol
                            </div>
                        )}
                    </div>

                    <div className="flex-1 min-w-0 flex flex-col border border-gray-300 rounded-lg overflow-hidden bg-white">
                        {isLoadingRight ? (
                            <div className="flex-1 flex items-center justify-center">
                                <Loader className="w-5 h-5 animate-spin text-blue-600" />
                            </div>
                        ) : rightRackStructure ? (
                            <InteractiveRackBody
                                rackStructure={rightRackStructure}
                                heatmapData={rightHeatmapData}
                                activeBinCode={activeBinCode}
                                activeIsBatch={activeIsBatch}
                                selectedBinCodes={selectedBinCodes}
                                onToggleSelect={onToggleSelect}
                                onBinContextMenu={onBinContextMenu}
                                emptyBinCodes={rightEmptyBinCodes}
                                activeMoveBins={rightActiveMoveBins}
                                title={rightRackNumber ? `Reol ${rightRackNumber}` : 'Høyre'}
                            />
                        ) : (
                            <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
                                Velg en reol
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <DragOverlay>
                {activeBinCode ? (
                    <div className="w-32 h-24 bg-blue-600 text-white border-2 border-blue-700 rounded shadow-xl flex flex-col items-center justify-center p-1.5 text-xs opacity-90 pointer-events-none">
                        {activeIsBatch ? (
                            <>
                                <CheckSquare className="w-4 h-4 mb-1" />
                                <div className="font-mono font-bold">{selectedBinCodes.size} valgte</div>
                            </>
                        ) : (
                            <>
                                <Move className="w-4 h-4 mb-1" />
                                <div className="font-mono font-bold">{activeBinCode}</div>
                            </>
                        )}
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};
